import { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import dayjs from "dayjs";
import { getLocale } from "react-i18nify";

import { getCityInfo, getQuotationInfo, placeOrder } from "src/api/lalamove";

import { styled as muiStyled } from "@mui/material/styles";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Autocomplete,
  IconButton,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormGroup,
  FormControlLabel,
  // FormLabel,
  Box,
  FormHelperText,
} from "@mui/material";
import MuiToggleButtonGroup, {
  toggleButtonGroupClasses,
} from "@mui/material/ToggleButtonGroup";
import MuiToggleButton, {
  toggleButtonClasses,
} from "@mui/material/ToggleButton";

import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InfoIcon from "@mui/icons-material/Info";
// import RadioUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
// import RadioCheckedIcon from "@mui/icons-material/RadioButtonChecked";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import BottonSheet from "src/components/ui/BottonSheet";
import QuantityInput from "src/components/ui/QuantityInput";
import UploadImage from "src/components/upload/dropzone/UploadImage";
import Checkbox from "src/components/core/Checkbox";
import SelectDateDialog from "src/components/order/SelectDateDialog";
import CustomMap from "src/components/ui/Maps/CustomMaps";
import Loading from "src/components/ui/Loading";

import device from "src/styles/device";
import font from "src/styles/font";
import {
  Black,
  DarkGrey,
  Focused,
  Grey,
  GreyStroke,
  Primary,
  Red,
  Secondary,
  White,
  White01,
  Yellow,
} from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency, convertPrice } from "src/common";
import { useDialog } from "src/context/DialogContext";
import { useSnackbar } from "notistack";
import {
  serviceType,
  categoriesText,
  handlingInstructions,
  specialRequests,
} from "src/common/lalamove";
import useDeviceSize from "src/hooks/useDeviceSize";
import { useAuth } from "src/hooks/useAuth";

const WrapperContent = styled.div`
  width: 100%;
  padding-bottom: 24px;
  overflow-y: auto;
  flex: 1 1 auto;
  -webkit-overflow-scrolling: touch;

  ::-webkit-scrollbar {
    width: 7px;
    height: 5px;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: var(--grey-color);
    border-radius: 100px;
  }
  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: var(--grey-color);
  }
  ::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const Content = styled.div`
  width: 100%;
  /* max-width: 530px; */
  margin: 0 auto;
  padding: 0 40px;
  position: relative;

  @media ${device.mobileL} {
    padding: 0 24px;
  }
`;

const ButtonGroup = styled(Stack)`
  flex: 0 0 auto;
  width: 100%;
  /* height: max-content; */
  background-color: #fff;
  z-index: 15;
  padding: 32px 24px;
`;

const ToggleButtonGroup = muiStyled(MuiToggleButtonGroup)(({ theme }) => ({
  [`&.${toggleButtonGroupClasses.root}`]: {
    gap: 8,
  },
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    // margin: theme.spacing(0.5),
    borderRadius: 16,
    background: `${White}`,
    color: `${Black}`,
    border: `1px solid ${GreyStroke}`,
  },
  [`& .${toggleButtonGroupClasses.grouped}:not(:last-of-type)`]: {
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
  },
  [`& .${toggleButtonGroupClasses.grouped}:not(:first-of-type)`]: {
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    borderLeft: `1px solid ${GreyStroke}`,
    marginLeft: 0,
  },
}));

const ToggleButton = muiStyled(MuiToggleButton)(({ theme }) => ({
  [`&.${toggleButtonClasses.root}:hover`]: {
    color: `${Primary}`,
    background: `${White01}`,
  },
  [`&.${toggleButtonClasses.root}.Mui-selected`]: {
    border: `2px solid ${Focused}`,
    background: `${Secondary}`,
  },
  [`&.${toggleButtonClasses.root}.Mui-disabled`]: {
    color: `${Grey}`,
    border: `1px solid ${GreyStroke}`,
    background: `${White01}`,
  },
}));

const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.md};
  font-weight: 500;
  color: ${Black};

  span {
    font-size: ${font.size.md};
    font-weight: 500;
    color: ${Red};
  }
`;

const DivProduct = styled.div`
  width: 100%;
`;

const WrapperProductHeader = styled.div`
  flex-direction: row;
  padding: 20px 0;
  display: flex;
  background: #f3f1f2;
`;

const WrapperProduct = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 5px 0 20px;
  border-bottom: 1px solid #d9d9d9;
  margin-left: 10px;
`;

const WrapperProductImage = styled.div`
  width: 80px;
  min-width: 80px;
  height: 80px;
  margin: 16px;
  /* flex: 1; */
`;

const ImgProduct = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 16px;
`;

const WrapperDesc = styled.div`
  /* width: 50%; */
  display: block;
  margin-right: auto;
  /* flex: 2; */
  padding-left: 16px;
  overflow: hidden;
`;

const ProductName = styled.p`
  font-size: 1rem;
  /* width: 80%; */
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductCode = styled.p`
  font-size: 0.75rem;
  /* width: 80%; */
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductType = styled.p`
  font-size: 0.75rem;
  font-weight: 400;
  /* width: 80%; */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WrapperProductField = styled.div`
  // margin-bottom: 16px;
  margin: auto;
  display: flex;
  justify-content: center;
  width: 65%;
`;

const WrapperAttachSlip = styled.div`
  width: 350px;
  height: auto;
  min-height: 350px;
  margin: 24px auto;
  background: #ffffff;
  border: 1px dashed #d9d9d9;
  border-radius: 24px;
  position: relative;
  /* display: flex;
  justify-content: center;
  align-items: center; */
`;

const SpanAttachSlip = styled.span`
  position: absolute;
  top: -10px;
  left: 12px;
  font-size: 0.75rem;
  font-weight: 400;
  color: #6c6c70;
  padding: 0 4px;
  background: #ffffff;
`;

const WrapperUploadImage = styled.div`
  width: 350px;
  min-height: 350px;
  padding: 10px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrapperImgSlip = styled.div`
  width: 100%;
  max-width: 300px;
  height: auto;
`;

const ImgSlip = styled.img`
  width: 100%;
  height: 100%;
  /* max-height: 300px; */
  object-fit: contain;
`;

const RemarkText = styled.span`
  font-size: ${font.size.xs};
  font-weight: 400;
  color: ${Black};
`;

const WrapperMapsContainer = styled.div`
  width: 100%;
  padding-top: calc(100% / (16 / 9));
  position: relative;

  @media ${device.tablet} {
    padding-top: calc(100% / (16 / 9));
  }
  @media ${device.mobileL} {
    padding-top: calc(100% / (8 / 10));
  }
`;

const MapsContainer = styled.div`
  position: absolute;
  border: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 16px;
`;

const TextOption = styled(Box)`
  width: 100%;
  color: ${DarkGrey};
  font-size: ${({ size }) => (size === "title" ? font.size.sm : font.size.xs)};
  font-weight: ${({ size }) => (size === "title" ? 500 : 400)};

  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const CountdownBox = styled.div`
  width: max-content;
  min-width: 250px;
  padding: 4px 16px;
  font-size: 1.25rem;
  color: #ff0000;
  border: 1px solid #252525;
  margin-left: auto;
  display: flex;
  /* flex-direction: column; */
  align-items: center;
  gap: 8px;

  p {
    font-size: 1.25rem;
    color: #ff0000;
    margin: 0;
  }
  span {
    font-size: 1rem;
    color: #252525;
  }

  @media ${device.mobileL} {
    width: 100%;
    margin: auto;
    justify-content: center;
  }
`;

const TimeoutBox = styled.div`
  width: max-content;
  padding: 16px;
  font-size: 1rem;
  /* border: 1px solid #252525; */
  border-radius: 8px;
  background: ${Primary};
  color: #ffffff;
  margin-left: auto;
`;

const WrapperCalOrder = styled.div`
  /* width: 100%; */
  background-color: ${White01};
  padding: 24px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  gap: 16px;
  /* border-bottom: 1px solid #d9d9d9; */
`;

const RowSumPrice = styled.div`
  margin-left: 0;
  margin-right: 0;
  justify-content: space-between;
  align-items: center;
  display: flex;
`;

const Price = styled.span`
  color: ${(props) => (props.fontColor ? props.fontColor : "#252525")};
  font-size: 1.125rem;
  font-weight: 400;

  ${({ desc, total }) =>
    desc && total
      ? css`
          font-size: 1rem;
          font-weight: 600;
        `
      : desc
        ? css`
            font-size: 1rem;
          `
        : total
          ? css`
              font-weight: 500;
              color: ${Primary};
            `
          : null}
`;

const rainbowAnimation = keyframes`
    0%,100% {
        background-position: 0 0;
    }

    50% {
        background-position: 100% 0;
    }
`;

const WarningText = styled.div`
  font-size: ${font.size.xs};
  font-weight: 400;
  color: ${Primary};

  /* background: linear-gradient(
    to right,
    #6666ff,
    #0099ff,
    #00ff00,
    #ff3399,
    #6666ff
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${rainbowAnimation} 6s ease-in-out infinite;
  background-size: 400% 100%; */
`;

const GetQuotationDialog = ({
  isOpen,
  userType = "admin",
  orderDetail,
  productList,
  onClose,
  onSuccess,
}) => {
  const { user, isLoggedIn } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();
  const [deviceWidth] = useDeviceSize();

  const [citiesData, setCitiesData] = useState([]);
  const [selectedCities, setSelectedCities] = useState(null);
  const [specialRequestsGroup, setSpecialRequestsGroup] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const initialQuotation = {
    // scheduleAt: '2024-08-29T14:30:00.00Z', // optional
    orderId: orderDetail?._id || null,
    scheduleAt: null,
    serviceType: null,
    specialRequests: [], // optional
    language: "th_TH",
    stops: [
      {
        coordinates: {
          lat: null,
          lng: null,
        },
        address: "",
      },
    ],
    // รายการของที่ซื้อ
    item: {
      quantity: 1,
      weight: null,
      categories: [],
      handlingInstructions: [],
    },
    isRouteOptimized: true,
  };
  const [quotationPayload, setQuotationPayload] = useState(initialQuotation);

  const initialOrder = {
    lalamove_quotation: null, // quotation detail
    quotationId: null,
    sender: {
      stopId: null,
      name: "",
      phone: "",
    },

    recipients: [
      {
        stopId: null,
        remarks: "", // optional
      },
    ],
    isPODEnabled: true, // optional
    // partner: 'Lalamove Partner 1', // optional

    delivery_sending_image: null, // Attach sending image
  };
  const [orderPayload, setOrderPayload] = useState(initialOrder);

  const [timer, setTimer] = useState(0);
  const [countTimer, setCountTimer] = useState({
    minute: "00",
    second: "00",
  });
  const [isQuotationChanged, setIsQuotationChanged] = useState(false);
  const [isScheduleDayBefore, setIsScheduleDayBefore] = useState(false);
  const [isCreateOrderDisabled, setCreateOrderDisabled] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      if (timer > 1) {
        let minutes = Math.floor((timer - 1) / 60);
        let seconds = (timer - 1) % 60;
        setCountTimer({
          minute: minutes > 10 ? minutes.toString() : "0" + minutes,
          second: seconds >= 10 ? seconds.toString() : "0" + seconds,
        });
        setTimer(timer - 1);
      } else if (timer === 1) {
        setCountTimer({
          minute: "00",
          second: "00",
        });
        setTimer(0);

        setOrderPayload({
          ...orderPayload,
          lalamove_quotation: null,
          quotationId: null,
          sender: {
            ...orderPayload.sender,
            stopId: null,
          },
          recipients: orderPayload.recipients.map((rec, i) => ({
            ...rec,
            stopId: null,
          })),
        });
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer]);

  useEffect(() => {
    if (!isOpen && !selectedCities) return;

    let stops = quotationPayload.stops;
    let sender = orderPayload?.sender;
    let findService = null;
    let scheduleDate = null;

    if (
      !!selectedCities &&
      !!orderDetail?.delivery_detail_group?.delivery_location?.sender
    ) {
      const senderData =
        orderDetail?.delivery_detail_group?.delivery_location?.sender;

      stops[0].address = senderData?.address;
      stops[0].coordinates.lat = senderData?.coordinates?.lat;
      stops[0].coordinates.lng = senderData?.coordinates?.lng;

      if (!!selectedCities) {
        findService = selectedCities?.services.find(
          (item) =>
            item.key ===
            orderDetail?.delivery_detail_group?.delivery_service_type,
        );
      }

      setSpecialRequestsGroup(
        Object.groupBy(findService?.specialRequests, ({ parent_type }) => {
          if (!!parent_type) return parent_type;
          return "normal";
        }),
      );

      let sender = orderPayload?.sender;
      sender.name = senderData?.name;
      sender.phone = senderData?.tel.replace("+66", "0");
    } else if (
      isLoggedIn &&
      user?.role_id === 3 &&
      user?.shipping_address?.length > 0
    ) {
      // ! get shipping data from user in user_profile in local storage

      stops[0].address = user?.shipping_address[0]?.address || "";
      stops[0].coordinates.lat =
        user?.shipping_address[0]?.coordinates?.lat || 13.6940782;
      stops[0].coordinates.lng =
        user?.shipping_address[0]?.coordinates?.lng || 100.6022862;

      let sender = orderPayload?.sender;
      sender.name = user?.shipping_address[0]?.name || "";
      sender.phone =
        !!user?.shipping_address[0]?.tel &&
        user?.shipping_address[0]?.tel[0] === "0"
          ? user?.shipping_address[0]?.tel.replace("+66", "0")
          : user?.shipping_address[0]?.tel || "";
    } else if (
      !!productList &&
      !!productList[0]?.product_choice_order?.product?.user?.shipping_address &&
      productList[0]?.product_choice_order?.product?.user?.shipping_address
        ?.length > 0
    ) {
      // get shipping data from user in product

      const senderData =
        productList[0]?.product_choice_order?.product?.user
          ?.shipping_address[0];

      stops[0].address = senderData?.address || "";
      stops[0].coordinates.lat = senderData?.coordinates?.lat || 13.6940782;
      stops[0].coordinates.lng = senderData?.coordinates?.lng || 100.6022862;

      let sender = orderPayload?.sender;
      sender.name = senderData?.name || "";
      sender.phone =
        !!senderData?.tel && senderData?.tel[0] === "0"
          ? senderData?.tel.replace("+66", "0")
          : senderData?.tel || "";
    } else {
      stops[0].coordinates.lat = 13.6940782;
      stops[0].coordinates.lng = 100.6022862;
    }

    if (!!orderDetail?.delivery_detail_group?.delivery_schedule_date) {
      if (
        dayjs(
          orderDetail?.delivery_detail_group?.delivery_schedule_date,
        ).isBefore(dayjs(new Date()))
      ) {
        setIsScheduleDayBefore(true);
      } else {
        scheduleDate =
          orderDetail?.delivery_detail_group?.delivery_schedule_date;
      }
    }

    setOrderPayload({
      ...orderPayload,
      sender: sender,
    });

    setQuotationPayload({
      ...quotationPayload,
      orderId: orderDetail?._id,
      stops: stops,
      scheduleAt: scheduleDate,
      serviceType: findService,
      specialRequests: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCities, isOpen]);

  const isFormValid = !!orderPayload?.quotationId && timer > 0;

  useEffect(() => {
    if (!isFormValid || isQuotationChanged) setCreateOrderDisabled(true);
    else setCreateOrderDisabled(false);
  }, [isFormValid, isQuotationChanged]);

  useEffect(() => {
    if (isCreateOrderDisabled) {
      setOrderPayload({
        ...orderPayload,
        lalamove_quotation: null,
        // quotationId: null,
      });
      setTimer(0);
      setCountTimer({
        minute: "00",
        second: "00",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateOrderDisabled]);

  useEffect(() => {
    if (!isQuotationChanged) {
      setIsQuotationChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationPayload]);

  const fetchCities = async () => {
    try {
      setIsLoading(true);
      const { data: res } = await getCityInfo();
      if (!!res) {
        setCitiesData(res.data);
        setSelectedCities(res.data.find((item) => item?.name === "Bangkok"));
      }
    } catch (error) {
      console.error("err: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSchedule = (e) => {
    if (e.target.value === "schedule") {
      openDialog({
        type: "custom",
        customDialog: (
          <SelectDateDialog
            onClose={() => closeDialog()}
            currentDate={quotationPayload?.scheduleAt}
            callbackDate={(value) => {
              setQuotationPayload({
                ...quotationPayload,
                scheduleAt: value,
              });
              closeDialog();
            }}
          />
        ),
      });
    } else {
      setQuotationPayload({
        ...quotationPayload,
        scheduleAt: null,
      });
    }
  };

  const onGetQuotation = async () => {
    try {
      setIsLoading(true);
      const { scheduleAt, ...payload } = quotationPayload;
      const { weight, ...item } = payload?.item;
      const quoInfo = {
        ...payload,
        ...(!!scheduleAt ? { scheduleAt } : {}),
        serviceType: payload.serviceType.key,
        item: {
          ...item,
          ...(!!weight ? { weight } : {}),
          quantity: item?.quantity.toString(),
        },
        stops: payload.stops.map((stop) => ({
          ...stop,
          coordinates: {
            lat: stop.coordinates.lat.toString(),
            lng: stop.coordinates.lng.toString(),
          },
        })),
      };

      const { data: res } = await getQuotationInfo({ data: quoInfo });

      if (!!res.data) {
        setIsQuotationChanged(false);

        setOrderPayload({
          ...orderPayload,
          lalamove_quotation: res.data,
          quotationId: res.data.quotationId,
          sender: {
            ...orderPayload.sender,
            stopId: res.data.stops[0].stopId,
          },
          recipients: orderPayload.recipients.map((rec, i) => ({
            ...rec,
            stopId: res.data.stops[i + 1].stopId,
          })),
        });
        setTimer(600);
        setCountTimer({
          minute: "10",
          second: "00",
        });
      }
    } catch (error) {
      console.error(error.response.data);

      error.response.data.errors.forEach((err) => {
        enqueueSnackbar(err.message, {
          variant: "error",
          autoHideDuration: 2000,
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      setIsLoading(true);
      const { delivery_sending_image, ...orderRes } = orderPayload;
      const orderParams = {
        ...orderRes,
        sender: {
          ...orderRes.sender,
          phone: orderRes.sender.phone.replace(orderRes.sender.phone[0], "+66"),
        },
        // recipients: orderRes.recipients.map((recipient) => ({
        //   ...recipient,
        //   phone: recipient.phone.replace(recipient.phone[0], "+66"),
        // })),
      };

      const { data: res } = await placeOrder({
        data: JSON.stringify(orderParams),
        delivery_sending_image: delivery_sending_image,
        order_id: orderDetail?._id,
        delivery_service_type: orderRes?.lalamove_quotation?.serviceType,
      });
      if (!!res.data) {
        enqueueSnackbar(`สร้าง order สำเร็จ`, {
          variant: "success",
          autoHideDuration: 2000,
        });
        onSuccess();
      }
    } catch (error) {
      console.error("--- err:", error);
      // console.error(error.response.data);
      error.response.data.errors.forEach((err) => {
        enqueueSnackbar(err.message, {
          variant: "error",
          autoHideDuration: 2000,
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const ProductOrder = () => {
    return productList?.map((product, index) => {
      return (
        <WrapperProduct key={`order-product-${index}`}>
          <Grid item xs={4.5} sx={{ display: "flex" }}>
            <WrapperProductImage>
              <ImgProduct
                src={product?.product_choice_order?.group_image?.product_display_image.replace(
                  ".com",
                  ".com/300x300",
                )}
                alt={
                  product?.product_choice_order?.group_image
                    ?.product_display_image
                }
              />
            </WrapperProductImage>

            <WrapperDesc>
              <ProductName>
                {product?.product_choice_order?.product?.item_name}
              </ProductName>

              <ProductCode>
                {product?.product_choice_order?.product?.item_code}
              </ProductCode>

              <ProductType>{product?.product_choice_order?.name}</ProductType>
            </WrapperDesc>
          </Grid>

          <Grid item xs={2.5} sx={{ display: "flex" }}>
            <WrapperProductField>
              <ProductName style={{ textAlign: "center" }}>
                {`${convertIntToCurrency(product?.price, false)}.-`}
              </ProductName>
            </WrapperProductField>
          </Grid>

          <Grid item xs={2.5} sx={{ display: "flex" }}>
            <WrapperProductField>
              <ProductName style={{ textAlign: "center" }}>
                {product?.volume}
              </ProductName>
            </WrapperProductField>
          </Grid>

          <Grid item xs={2.5} sx={{ display: "flex" }}>
            <WrapperProductField>
              <ProductName style={{ textAlign: "center" }}>
                {`${convertIntToCurrency(
                  product?.price * product?.volume,
                  false,
                )}.-`}
              </ProductName>
            </WrapperProductField>
          </Grid>
        </WrapperProduct>
      );
    });
  };

  const SellerProductOrder = () => {
    return productList?.map((product, index) => {
      return (
        <WrapperProduct key={`order-product-${index}`}>
          <Grid item xs={7} sx={{ display: "flex" }}>
            <WrapperProductImage>
              <ImgProduct
                src={product?.product_choice_order?.group_image?.product_display_image.replace(
                  ".com",
                  ".com/300x300",
                )}
                alt={
                  product?.product_choice_order?.group_image
                    ?.product_display_image
                }
              />
            </WrapperProductImage>

            <WrapperDesc>
              <ProductName>
                {product?.product_choice_order?.product?.item_name}
              </ProductName>

              <ProductCode>
                {product?.product_choice_order?.product?.item_code}
              </ProductCode>

              <ProductType>{product?.product_choice_order?.name}</ProductType>
            </WrapperDesc>
          </Grid>

          <Grid item xs={5} sx={{ display: "flex" }}>
            <WrapperProductField>
              <ProductName style={{ textAlign: "center" }}>
                {product?.volume}
              </ProductName>
            </WrapperProductField>
          </Grid>
        </WrapperProduct>
      );
    });
  };

  const LalamoveContent = () => {
    return (
      <Grid container spacing={3}>
        {/* Product table */}
        {userType === "admin" && (
          <Grid item xs={12}>
            <DivProduct>
              <WrapperProductHeader>
                <Grid
                  item
                  xs={4.5}
                  sx={{
                    margin: "auto",
                    textAlign: "center",
                    fontSize: {
                      md: "1.125rem",
                      sm: "1rem",
                      xs: "0.875rem",
                    },
                  }}
                >
                  {getLocale() === "th" ? "สินค้า" : "Product"}
                </Grid>

                <Grid
                  item
                  xs={2.5}
                  sx={{
                    margin: "auto",
                    textAlign: "center",
                    fontSize: {
                      md: "1.125rem",
                      sm: "1rem",
                      xs: "0.875rem",
                    },
                  }}
                >
                  {getLocale() === "th" ? "ราคา" : "Price"}
                </Grid>

                <Grid
                  item
                  xs={2.5}
                  sx={{
                    margin: "auto",
                    textAlign: "center",
                    fontSize: {
                      md: "1.125rem",
                      sm: "1rem",
                      xs: "0.875rem",
                    },
                  }}
                >
                  {getLocale() === "th" ? "จำนวน" : "Quantity"}
                </Grid>

                <Grid
                  item
                  xs={2.5}
                  sx={{
                    margin: "auto",
                    textAlign: "center",
                    fontSize: {
                      md: "1.125rem",
                      sm: "1rem",
                      xs: "0.875rem",
                    },
                  }}
                >
                  {getLocale() === "th" ? "ราคารวม" : "Total Price"}
                </Grid>
              </WrapperProductHeader>
            </DivProduct>

            <DivProduct>{ProductOrder()}</DivProduct>
          </Grid>
        )}

        {userType === "seller" && (
          <Grid item xs={12}>
            <DivProduct>
              <WrapperProductHeader>
                <Grid
                  item
                  xs={7}
                  sx={{
                    pl: 3,
                    margin: "auto",
                    // textAlign: "center",
                    fontSize: {
                      md: "1.125rem",
                      sm: "1rem",
                      xs: "0.875rem",
                    },
                  }}
                >
                  {getLocale() === "th" ? "สินค้า" : "Product"}
                </Grid>

                <Grid
                  item
                  xs={5}
                  sx={{
                    margin: "auto",
                    textAlign: "center",
                    fontSize: {
                      md: "1.125rem",
                      sm: "1rem",
                      xs: "0.875rem",
                    },
                  }}
                >
                  {getLocale() === "th" ? "จำนวน" : "Quantity"}
                </Grid>
              </WrapperProductHeader>
            </DivProduct>

            <DivProduct>{SellerProductOrder()}</DivProduct>
          </Grid>
        )}

        {/* Selected Province area */}
        <Grid item xs={12}>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <WrapperTitle>
                จังหวัดที่ต้องเข้ารับพัสดุ<span>*</span>
              </WrapperTitle>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                // freeSolo
                aria-required={true}
                id="city-select"
                getOptionSelected={(option, value) =>
                  option?.locode === value?.locode
                }
                isOptionEqualToValue={(option, value) =>
                  option?.locode === value?.locode
                }
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      {option?.name === "Bangkok"
                        ? "กรุงเทพและปริมณฑล"
                        : option?.name === "Chonburi"
                          ? "ชลบุรี"
                          : ""}
                    </li>
                  );
                }}
                options={citiesData}
                value={selectedCities}
                onChange={(_, newValue) => {
                  setSelectedCities(newValue);
                  setQuotationPayload({
                    ...quotationPayload,
                    serviceType: null,
                    specialRequests: [],
                  });
                }}
                // loading={isLoading}
                getOptionLabel={(option) =>
                  (option?.name === "Bangkok"
                    ? "กรุงเทพและปริมณฑล"
                    : option?.name === "Chonburi"
                      ? "ชลบุรี"
                      : "") || ""
                }
                sx={{
                  // mt: 2,
                  // mb: 3,
                  width: "100%",
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="เลือกจังหวัด" />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        {!!selectedCities && (
          <>
            {/* Pickup detail */}
            <Grid item xs={12}>
              <Grid container spacing={2} sx={{ py: 2 }}>
                <Grid item xs={12}>
                  <WrapperTitle>
                    จุดรับสินค้า<span>*</span>
                  </WrapperTitle>
                </Grid>

                <Grid item xs={12}>
                  <WrapperMapsContainer>
                    <MapsContainer>
                      <CustomMap
                        markerLocation={{
                          lat: quotationPayload?.stops[0]?.coordinates?.lat,
                          lng: quotationPayload?.stops[0]?.coordinates?.lng,
                        }}
                        changeMarkerLocation={(value) => {
                          let stopData = quotationPayload.stops;
                          stopData[0].coordinates = value;
                          setQuotationPayload({
                            ...quotationPayload,
                            stops: stopData,
                          });
                        }}
                        changeSeletedAddress={(value) => {
                          let stopData = quotationPayload.stops;
                          stopData[0].address = value;
                          setQuotationPayload({
                            ...quotationPayload,
                            stops: stopData,
                          });
                        }}
                        inputPlaceholder="จุดรับสินค้า"
                      />
                    </MapsContainer>
                  </WrapperMapsContainer>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="รายละเอียดที่อยู่"
                    variant="outlined"
                    fullWidth
                    value={quotationPayload?.stops[0]?.address}
                    onChange={(e) => {
                      let stopData = quotationPayload.stops;
                      stopData[0].address = e.target.value;
                      setQuotationPayload({
                        ...quotationPayload,
                        stops: stopData,
                      });
                    }}
                    error={!!!quotationPayload?.stops[0]?.address}
                    helperText={
                      !!!quotationPayload?.stops[0]?.address
                        ? `กรุณากรอกที่อยู่`
                        : ``
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="ชื่อผู้ส่ง"
                    variant="outlined"
                    fullWidth
                    value={orderPayload?.sender?.name}
                    onChange={(e) => {
                      let sender = orderPayload.sender;
                      sender.name = e.target.value;
                      setOrderPayload({
                        ...orderPayload,
                        sender: sender,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="เบอร์โทรศัพท์"
                    variant="outlined"
                    fullWidth
                    value={orderPayload?.sender?.phone}
                    onChange={(e) => {
                      let sender = orderPayload.sender;
                      sender.phone = e.target.value;
                      setOrderPayload({
                        ...orderPayload,
                        sender: sender,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    // freeSolo
                    fullWidth
                    aria-required={true}
                    id="service-type-select"
                    getOptionSelected={(option, value) =>
                      option?.key === value?.key
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.key === value?.key
                    }
                    options={selectedCities?.services?.filter(
                      (item) => item.key !== "LD_4_DOOR_PICKUP_TRUCK",
                    )}
                    value={quotationPayload?.serviceType}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={`service-type-select-${option?.key}`}>
                        <Box
                          sx={{
                            width: "100%",
                            py: 1,
                            display: "flex",
                            alignItems: "center",
                            // justifyContent: "space-around",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: "40px",
                              height: "40px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <img
                              style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "cover",
                                objectPosition: "center",
                                borderRadius: "8px",
                              }}
                              src={`https://xsurface-images.s3.ap-southeast-1.amazonaws.com/lalamove-icon/${option?.key}.png`}
                              alt={`${option?.key}-img`}
                            />
                          </Box>

                          <Box>
                            <TextOption size="title">
                              {serviceType(option?.key)}
                            </TextOption>

                            <TextOption>
                              {`ขนาดสินค้าแนะนำ : ${option?.dimensions?.length?.value} x ${option?.dimensions?.width?.value} x ${option?.dimensions?.height?.value} เมตร`}
                            </TextOption>
                            <TextOption>
                              {`น้ำหนักสูงสุด ${option?.load?.value} กิโลกรัม`}
                            </TextOption>
                          </Box>
                        </Box>
                      </li>
                    )}
                    onChange={(_, newValue) => {
                      setQuotationPayload({
                        ...quotationPayload,
                        serviceType: newValue,
                        specialRequests: [],
                      });
                      setSpecialRequestsGroup(
                        Object.groupBy(
                          newValue?.specialRequests,
                          ({ parent_type }) => {
                            if (!!parent_type) return parent_type;
                            return "normal";
                          },
                        ),
                      );
                    }}
                    getOptionLabel={(option) => serviceType(option?.key)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth label="เลือกประเภทรถ" />
                    )}
                  />
                  <Stack
                    alignItems="center"
                    direction="row"
                    // justifyContent="center"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <InfoIcon style={{ color: Yellow }} />
                    <RemarkText>กรุณาเลือกประเภทรถให้เข้ากับสินค้า</RemarkText>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="schedule-date-label">
                      เวลาเข้ารับสินค้า
                    </InputLabel>
                    <Select
                      labelId="schedule-date-label"
                      id="schedule-date"
                      label="เวลาเข้ารับสินค้า"
                      value={
                        !!quotationPayload?.scheduleAt
                          ? quotationPayload?.scheduleAt
                          : "-"
                      }
                      onChange={handleSchedule}
                    >
                      <MenuItem value={"-"}>รับทันที</MenuItem>
                      <MenuItem value={"schedule"}>ภายหลัง</MenuItem>
                      <MenuItem
                        sx={{ display: "none" }}
                        value={
                          !!quotationPayload?.scheduleAt &&
                          quotationPayload?.scheduleAt
                        }
                      >
                        {dayjs(quotationPayload?.scheduleAt).format(
                          "DD/MM/YYYY - hh:mm A",
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {isScheduleDayBefore && (
                    <Stack
                      alignItems="center"
                      direction="row"
                      // justifyContent="center"
                      spacing={1}
                      sx={{ mt: 1 }}
                    >
                      <InfoIcon style={{ color: Primary }} />
                      <WarningText>
                        {getLocale() === "th"
                          ? `คำสั่งซื้อนี้เกินระยะเวลาที่ลูกค้าต้องการรับสินค้ากรุณาติดต่อเจ้าหน้าที่ XSURFACEเพื่อดำเนินการนัดหมายกับลูกค้าอีกครั้ง`
                          : `The order has exceeded the desired delivery time frame. Please contact XSURFACE staff to reschedule the delivery with the customer.`}
                      </WarningText>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {!!quotationPayload?.serviceType && (
              <>
                {/* Product delivered detail */}
                <Grid item xs={12}>
                  <Grid container spacing={3}>
                    {/* categories */}
                    <Grid item xs={12}>
                      <Stack spacing={2}>
                        <WrapperTitle>ประเภทสินค้าที่จัดส่ง</WrapperTitle>

                        <Autocomplete
                          multiple
                          fullWidth
                          disableCloseOnSelect
                          aria-required={true}
                          id="product-category-select"
                          getOptionSelected={(option, value) =>
                            option === value
                          }
                          isOptionEqualToValue={(option, value) =>
                            option === value
                          }
                          options={
                            [
                              ...quotationPayload?.serviceType
                                ?.deliveryItemSpecification?.categories,
                              "OTHERS",
                            ] || []
                          }
                          value={quotationPayload?.item?.categories}
                          onChange={(_, newValue) => {
                            setQuotationPayload({
                              ...quotationPayload,
                              item: {
                                ...quotationPayload?.item,
                                categories: newValue,
                              },
                            });
                          }}
                          getOptionLabel={(option) => categoriesText(option)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label="ระบุประเภทสินค้า"
                            />
                          )}
                        />
                      </Stack>
                    </Grid>

                    {/* volume */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={3}>
                        <WrapperTitle>
                          จำนวนพัสดุ<span>*</span>
                        </WrapperTitle>

                        <QuantityInput
                          onChange={(value) => {
                            let itemDetail = quotationPayload?.item;
                            itemDetail.quantity = value;
                            setQuotationPayload({
                              ...quotationPayload,
                              item: itemDetail,
                            });
                          }}
                          value={quotationPayload?.item?.quantity}
                        />
                      </Stack>

                      <FormHelperText error={true}>
                        กรุณาระบุจำนวนพัสดุ
                      </FormHelperText>
                    </Grid>

                    {/* weight */}
                    {quotationPayload.serviceType?.key === "MOTORCYCLE" && (
                      <Grid item xs={12}>
                        <Stack spacing={3}>
                          <WrapperTitle>
                            น้ำหนักรวม<span>*</span>
                          </WrapperTitle>

                          <ToggleButtonGroup
                            value={quotationPayload?.item?.weight}
                            exclusive
                            onChange={(_, value) => {
                              let itemDetail = quotationPayload?.item;
                              itemDetail.weight = value;
                              setQuotationPayload({
                                ...quotationPayload,
                                item: itemDetail,
                              });
                            }}
                            aria-label="text alignment"
                          >
                            <ToggleButton
                              value="LESS_THAN_5KG"
                              aria-label="less than 1kg"
                            >
                              น้อยกว่า 1 Kg
                            </ToggleButton>
                            <ToggleButton
                              value="1_KG_TO_5_KG"
                              aria-label="1kg to 5kg"
                            >
                              1 Kg - 5 Kg
                            </ToggleButton>
                            <ToggleButton
                              value="5_KG_TO_10_KG"
                              aria-label="5kg to 10kg"
                            >
                              5 Kg - 10 Kg
                            </ToggleButton>
                            <ToggleButton
                              value="10_KG_TO_15_KG"
                              aria-label="10kg to 15kg"
                            >
                              10 Kg - 15 Kg
                            </ToggleButton>
                            <ToggleButton
                              value="15_KG_TO_20_KG"
                              aria-label="15kg to 20kg"
                            >
                              15 Kg - 20 Kg
                            </ToggleButton>
                            <ToggleButton
                              value="MORE_THAN_5KG"
                              aria-label="more than 20kg"
                            >
                              มากกว่านี้
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                      </Grid>
                    )}

                    {/* handling instructions */}
                    <Grid item xs={12}>
                      <Stack spacing={2}>
                        <WrapperTitle>คำแนะนำในการขนส่งสินค้า</WrapperTitle>

                        <FormControl sx={{ width: "100%" }}>
                          <FormGroup
                            row
                            name="special-request-checkbox-group"
                            sx={{
                              ml: "-10px",
                              width: "calc(100% + 10px)",
                              fontSize: font.size.sm,
                              color: "var(--secondary-color)",
                              "& .MuiFormControlLabel-root": {
                                m: 0,
                                mb: 1,
                                alignItems: "flex-start",
                                "&.MuiRadio-root": {
                                  width: "auto",
                                },
                                "& .MuiFormControlLabel-label": {
                                  pt: 1,
                                },
                                ":last-child": {
                                  mb: 0,
                                },
                              },
                            }}
                            onChange={(e) => {
                              let itemDetail = quotationPayload?.item;
                              let handlingInstructions =
                                itemDetail?.handlingInstructions;
                              if (e.target.checked) {
                                handlingInstructions.push(e.target.value);
                              } else {
                                handlingInstructions =
                                  handlingInstructions.filter(
                                    (item) => item !== e.target.value,
                                  );
                              }
                              setQuotationPayload({
                                ...quotationPayload,
                                item: {
                                  ...itemDetail,
                                  handlingInstructions: handlingInstructions,
                                },
                              });
                            }}
                          >
                            {/* // ! map item */}
                            {quotationPayload?.serviceType?.deliveryItemSpecification?.handlingInstructions?.map(
                              (item, index) => (
                                <FormControlLabel
                                  key={`special-request-${item}`}
                                  label={handlingInstructions(item)}
                                  sx={{
                                    width: "100%",
                                  }}
                                  control={
                                    <Checkbox
                                      sx={{
                                        p: 1,
                                        color: "var(--primary-color)",
                                        "&.Mui-checked": {
                                          color: "var(--primary-color)",
                                        },
                                        "& .MuiSvgIcon-root": {
                                          fontSize: 24,
                                        },
                                      }}
                                      value={item}
                                      checked={
                                        quotationPayload?.item?.handlingInstructions?.includes(
                                          item,
                                        )
                                          ? true
                                          : false
                                      }
                                    />
                                  }
                                />
                              ),
                            )}
                          </FormGroup>
                        </FormControl>
                      </Stack>
                    </Grid>

                    {/* special request */}
                    <Grid item xs={12}>
                      <Stack spacing={2}>
                        <WrapperTitle>คำร้องขอเพิ่มเติม</WrapperTitle>

                        <FormControl sx={{ width: "100%" }}>
                          {Object.keys(specialRequestsGroup)
                            .sort((a, b) => (a === "normal" ? -1 : 1))
                            ?.map((key, i) => {
                              // if (key === "normal") {
                              return (
                                <>
                                  <FormGroup
                                    row
                                    key={`special-request-${i}`}
                                    name="special-request-checkbox-group"
                                    sx={{
                                      ml: "-10px",
                                      width: "calc(100% + 10px)",
                                      fontSize: font.size.sm,
                                      color: "var(--secondary-color)",
                                      "& .MuiFormControlLabel-root": {
                                        m: 0,
                                        mb: 1,
                                        alignItems: "flex-start",
                                        "&.MuiRadio-root": {
                                          width: "auto",
                                        },
                                        "& .MuiFormControlLabel-label": {
                                          pt: 1,
                                        },
                                        ":last-child": {
                                          mb: 0,
                                        },
                                      },
                                    }}
                                    onChange={(e) => {
                                      let specialRequests =
                                        quotationPayload?.specialRequests;
                                      if (e.target.checked) {
                                        specialRequests.push(e.target.value);
                                      } else {
                                        specialRequests =
                                          specialRequests.filter(
                                            (item) => item !== e.target.value,
                                          );
                                      }
                                      setQuotationPayload({
                                        ...quotationPayload,
                                        specialRequests: specialRequests,
                                      });
                                    }}
                                  >
                                    {specialRequestsGroup[key]
                                      ?.sort((a, b) =>
                                        a.name
                                          .toLowerCase()
                                          .localeCompare(b.name.toLowerCase()),
                                      )
                                      ?.map((item, i) => (
                                        <FormControlLabel
                                          key={`special-request-${item?.name}`}
                                          label={specialRequests(item?.name)}
                                          sx={{
                                            width: "100%",
                                          }}
                                          control={
                                            <Checkbox
                                              sx={{
                                                p: 1,
                                                color: "var(--primary-color)",
                                                "&.Mui-checked": {
                                                  color: "var(--primary-color)",
                                                },
                                                "& .MuiSvgIcon-root": {
                                                  fontSize: 24,
                                                },
                                              }}
                                              value={item?.name}
                                              checked={
                                                quotationPayload?.specialRequests?.includes(
                                                  item?.name,
                                                )
                                                  ? true
                                                  : false
                                              }
                                            />
                                          }
                                        />
                                      ))}
                                  </FormGroup>
                                </>
                              );
                              // }
                              // else {
                              //   return (
                              //     <>
                              //       <FormLabel
                              //         id={`${key}-buttons-group-label`}
                              //         sx={{
                              //           color: DarkGrey,
                              //           lineHeight: 3,
                              //           borderTop: `1px solid ${GreyStroke}`,
                              //           marginTop: 1,
                              //           "&.Mui-focused": {
                              //             color: DarkGrey,
                              //           },
                              //         }}
                              //       >
                              //         {key} ?
                              //       </FormLabel>
                              //       <FormGroup
                              //         row
                              //         name="special-request-checkbox-group"
                              //         sx={{
                              //           // ml: "-10px",
                              //           // width: "calc(100% + 10px)",
                              //           width: "100%",
                              //           fontSize: font.size.sm,
                              //           color: "var(--secondary-color)",
                              //           "& .MuiFormControlLabel-root": {
                              //             m: 0,
                              //             mb: 1,
                              //             alignItems: "flex-start",
                              //             "&.MuiRadio-root": {
                              //               width: "auto",
                              //             },
                              //             "& .MuiFormControlLabel-label": {
                              //               pt: 1,
                              //             },
                              //             ":last-child": {
                              //               mb: 0,
                              //             },
                              //           },
                              //         }}
                              //         onChange={(e) => {
                              //           let reqList = specialRequestsGroup[
                              //             key
                              //           ].map(({ name }) => name);
                              //           let specialRequests =
                              //             quotationPayload?.specialRequests.filter(
                              //               (item) => !reqList.includes(item)
                              //             );

                              //           if (e.target.checked) {
                              //             specialRequests.push(
                              //               e.target.value
                              //             );
                              //           }

                              //           setQuotationPayload({
                              //             ...quotationPayload,
                              //             specialRequests: specialRequests,
                              //           });
                              //         }}
                              //       >
                              //         {specialRequestsGroup[key]
                              //           ?.sort((a, b) =>
                              //             a.name
                              //               .toLowerCase()
                              //               .localeCompare(
                              //                 b.name.toLowerCase()
                              //               )
                              //           )
                              //           ?.map((item, i) => (
                              //             <FormControlLabel
                              //               key={`special-request-${item?.name}`}
                              //               label={specialRequests(
                              //                 item?.name
                              //               )}
                              //               sx={{
                              //                 width: "100%",
                              //               }}
                              //               control={
                              //                 <Checkbox
                              //                   sx={{
                              //                     p: 1,
                              //                     color:
                              //                       "var(--primary-color)",
                              //                     "&.Mui-checked": {
                              //                       color:
                              //                         "var(--primary-color)",
                              //                     },
                              //                     "& .MuiSvgIcon-root": {
                              //                       fontSize: 24,
                              //                     },
                              //                   }}
                              //                   icon={<RadioUncheckedIcon />}
                              //                   checkedIcon={
                              //                     <RadioCheckedIcon />
                              //                   }
                              //                   value={item?.name}
                              //                   checked={
                              //                     quotationPayload?.specialRequests?.includes(
                              //                       item?.name
                              //                     )
                              //                       ? true
                              //                       : false
                              //                   }
                              //                 />
                              //               }
                              //             />
                              //           ))}
                              //       </FormGroup>
                              //     </>
                              //   );
                              // }
                            })}
                        </FormControl>
                      </Stack>
                    </Grid>

                    {/* Note */}
                    {/* <Grid item xs={12}>
                      <WrapperTitle style={{ marginBottom: 8 }}>
                        หมายเหตุ
                      </WrapperTitle>

                      <TextField
                        id="remark-input"
                        placeholder="หมายเหตุถึงคนขับ (ไม่บังคับ)"
                        fullWidth
                        multiline
                        rows={2}
                        onChange={(e) => {
                          let recipients = orderPayload.recipients;
                          recipients[0].remarks = e.target.value;
                          setOrderPayload({
                            ...orderPayload,
                            recipients: recipients,
                          });
                        }}
                        value={orderPayload?.recipients[0]?.remarks}
                      />
                    </Grid> */}

                    {/* get quotation */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Stack
                        justifyContent={{ xs: "center", sm: "flex-start" }}
                        direction="row"
                      >
                        <Button
                          variant="secondary"
                          // size="large"
                          onClick={() => {
                            onGetQuotation();
                          }}
                          sx={{
                            minWidth: 160,
                            width: "max-content",
                          }}
                          startIcon={<LocalShippingIcon />}
                        >
                          ตรวจสอบรายการ
                        </Button>
                      </Stack>
                    </Grid>

                    {!!orderPayload?.lalamove_quotation ? (
                      <Grid item xs={12} sm={6} md={8}>
                        {timer > 0 ? (
                          <CountdownBox>
                            <span>กรุณาทำรายการภายใน</span>
                            <p>{`${countTimer.minute}:${countTimer.second}`}</p>
                          </CountdownBox>
                        ) : (
                          <TimeoutBox>
                            หมดเวลาการทำรายการ กรุณาคำนวนราคาใหม่อีกครั้ง
                          </TimeoutBox>
                        )}
                      </Grid>
                    ) : null}

                    {!!orderPayload.lalamove_quotation && timer > 0 ? (
                      <>
                        <Grid item xs={12}>
                          <WrapperCalOrder>
                            <RowSumPrice>
                              <Price desc>ค่าจัดส่ง</Price>
                              <Price>
                                {convertPrice(
                                  Number(
                                    orderPayload?.lalamove_quotation
                                      ?.priceBreakdown?.base,
                                  ) +
                                    Number(
                                      orderPayload?.lalamove_quotation
                                        ?.priceBreakdown?.extraMileage || 0,
                                    ),
                                )}
                              </Price>
                            </RowSumPrice>

                            <RowSumPrice>
                              <Price desc>ค่าบริการคำร้องขอเพิ่มเติม</Price>
                              <Price>
                                {convertPrice(
                                  orderPayload?.lalamove_quotation
                                    ?.priceBreakdown?.specialRequests || 0,
                                )}
                              </Price>
                            </RowSumPrice>

                            <RowSumPrice>
                              <Price desc total>
                                รวม
                              </Price>
                              <Price total>
                                {convertPrice(
                                  orderPayload?.lalamove_quotation
                                    ?.priceBreakdown?.total,
                                )}
                              </Price>
                            </RowSumPrice>
                          </WrapperCalOrder>
                        </Grid>

                        {/* attach proof of delivery */}
                        <Grid item xs={12}>
                          <WrapperAttachSlip>
                            <SpanAttachSlip>แนบหลักฐานการส่งของ</SpanAttachSlip>

                            {checkNotEmpty(
                              orderPayload?.delivery_sending_image,
                            ) && (
                              <IconButton
                                sx={{
                                  padding: "3px",
                                  position: "absolute",
                                  top: "10px",
                                  right: "10px",
                                  zIndex: 10,
                                  backgroundColor: "#d9d9d999",
                                  ":hover": {
                                    backgroundColor: "#d9d9d9db",
                                  },
                                }}
                                onClick={() => {
                                  const dataUp = orderPayload;
                                  dataUp.delivery_sending_image = "";
                                  setOrderPayload({ ...dataUp });
                                }}
                              >
                                <CloseIcon
                                  sx={{
                                    color: "#e04132",
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            )}

                            <UploadImage
                              callBackFile={(fileCallBlack) => {
                                const dataUp = orderPayload;
                                dataUp.delivery_sending_image = fileCallBlack;
                                fileCallBlack !== "" &&
                                  setOrderPayload({ ...dataUp });
                              }}
                              closeAlert={false}
                            >
                              {checkNotEmpty(
                                orderPayload?.delivery_sending_image,
                              ) ? (
                                <WrapperUploadImage>
                                  <WrapperImgSlip>
                                    <ImgSlip
                                      src={
                                        orderPayload?.delivery_sending_image
                                          ?.fileData
                                      }
                                      alt="delivery_sending_image"
                                    />
                                  </WrapperImgSlip>
                                </WrapperUploadImage>
                              ) : (
                                <WrapperUploadImage>
                                  <Button
                                    sx={{
                                      padding: "8px 16px",
                                      fontSize: "0.875rem",
                                      "&.MuiButton-outlined": {
                                        border: "1px solid #d9d9d9",
                                      },
                                    }}
                                    variant="outlined"
                                    startIcon={<AddCircleIcon />}
                                  >
                                    {getLocale() === "th"
                                      ? "เลือกรูปจากเครื่องของคุณ"
                                      : "Select image"}
                                  </Button>
                                </WrapperUploadImage>
                              )}
                            </UploadImage>
                          </WrapperAttachSlip>

                          <Stack
                            alignItems="center"
                            direction="row"
                            justifyContent="center"
                            spacing={1}
                          >
                            <InfoIcon style={{ color: Yellow }} />
                            <RemarkText>
                              กรุณาแนบหลักฐานการส่งของเพื่อเป็นหลักฐานและป้องกันปัญหาที่อาจจะเกิดขึ้น
                            </RemarkText>
                          </Stack>
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    );
  };

  const handleOnClose = () => {
    openDialog({
      type: "confirm",
      title: `คุณต้องการออกจากหน้านี้ใช่หรือไม่`,
      onConfirm: () => {
        setQuotationPayload(initialQuotation);
        setOrderPayload(initialOrder);
        setIsQuotationChanged(true);
        onClose();
      },
      maxWidth: 342,
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      {deviceWidth > 835 ? (
        <>
          <Dialog
            onClose={handleOnClose}
            aria-labelledby="customized-dialog-title"
            open={isOpen}
            sx={{
              "& .MuiDialog-container": {
                "& .MuiPaper-root": {
                  width: "100%",
                  maxWidth: 800,
                  borderRadius: 4,
                },
              },
              "& .MuiDialogContent-root": {
                pt: 1,
                pb: 4,
              },
            }}
          >
            <DialogTitle sx={{ p: 3 }}>
              <WrapperTitle>ตรวจสอบรายละเอียดสินค้า</WrapperTitle>

              <IconButton
                aria-label="close"
                onClick={handleOnClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>{LalamoveContent()}</DialogContent>

            <DialogActions
              sx={{
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              {isCreateOrderDisabled && !!orderPayload?.quotationId && (
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <InfoIcon style={{ color: Primary }} />
                  <RemarkText>
                    ข้อมูลมีการเปลี่ยนแปลง กรุณากดปุ่ม "ตรวจสอบรายการ"
                    อีกครั้งก่อนทำรายการ
                  </RemarkText>
                </Stack>
              )}

              <Button
                variant="secondary"
                // size="large"
                onClick={() => {
                  openDialog({
                    type: "confirm",
                    title: `คุณต้องการเรียกรถเข้ารับสินค้าใช่หรือไม่?`,
                    onConfirm: () => {
                      createOrder();
                    },
                    maxWidth: 342,
                  });
                }}
                sx={{
                  minWidth: 160,
                }}
                startIcon={<LocalShippingIcon />}
                disabled={isCreateOrderDisabled}
              >
                เรียกรถเข้ารับสินค้า
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <>
          <BottonSheet
            title={`ตรวจสอบรายละเอียดสินค้า`}
            onClose={handleOnClose}
            open={isOpen}
          >
            <WrapperContent>
              <Content>{LalamoveContent()}</Content>
            </WrapperContent>

            <ButtonGroup
              // flexDirection={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              columnGap={2}
            >
              {isCreateOrderDisabled && !!orderPayload?.quotationId && (
                <Stack
                  alignItems="center"
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <InfoIcon style={{ color: Primary }} />
                  <RemarkText>
                    ข้อมูลมีการเปลี่ยนแปลง กรุณากดปุ่ม "ตรวจสอบรายการ"
                    อีกครั้งก่อนทำรายการ
                  </RemarkText>
                </Stack>
              )}

              <Button
                variant="secondary"
                // size="large"
                onClick={() => {
                  openDialog({
                    type: "confirm",
                    title: `คุณต้องการเรียกรถเข้ารับสินค้าใช่หรือไม่?`,
                    onConfirm: () => {
                      createOrder();
                    },
                    maxWidth: 342,
                  });
                }}
                sx={{
                  minWidth: 160,
                }}
                startIcon={<LocalShippingIcon />}
                disabled={isCreateOrderDisabled}
              >
                เรียกรถเข้ารับสินค้า
              </Button>
            </ButtonGroup>
          </BottonSheet>
        </>
      )}

      {isLoading && <Loading />}
    </>
  );
};

export default GetQuotationDialog;
